<template>
  <div class="cnc-radio-button">
    <el-radio v-for="choice in choices" :key="choice.id" v-model="val" :class="`${sizeRadio[size]} ${isReadOnly ? 'read-only' : ''} ${isError ? 'is-error' : ''
      }`" :disabled="disabled" :label="choice.label" @change="(val) => $emit('change', val)">
      <div class="fragment-name">
        <div class="txt-radio-name">
          <div v-if="choice.name && choice.name.length">{{ choice.name }}</div>
          <slot v-else class="txt-radio-name" />
        </div>
        <div v-if="rightIcon">
          <SvgIcon v-if="rightIcon.type === 'svg'" :name="rightIcon.name" />
          <Icon v-else :name="rightIcon.name" />
        </div>
        </div>
        <div v-if="choice.desc" class="txt-radio-desc" @click="($event) => { $event.preventDefault() }">{{ choice.desc }}</div>
    </el-radio>
  </div>
</template>

<script>
export default {
  components: {
    Icon: () => import("./Icon.vue"),
    SvgIcon: () => import("./SvgIcon.vue"),
  },
  props: {
    value: { type: Boolean },
    choices: { type: Array, default: () => [] },
    rightIcon: { type: Object },
    size: { type: String, default: "medium" },
    label: { type: String },
    desc: { type: String },
    disabled: { type: Boolean, default: false },
    isReadOnly: { type: Boolean },
    isError: { type: Boolean },
  },
  data () {
    return {
      val: this.value,
      sizeRadio: {
        medium: "medium",
        small: "small",
      },
    }
  },
  computed: {
    isDisabled () {
      return this.isReadOnly || this.disabled
    },
  },
  watch: {
    value (val, oldVal) {
      this.val = val
    },
    val (val) {
      this.$emit("update:value", val)
    },
  },
}
</script>

<style lang="scss">
.cnc-radio-button {
  .el-radio {
    display: flex;
    white-space: normal;
    margin-right: 0px;

    .fragment-name {
      display: flex;
      align-items: center;
      gap: $spacing-3xs;
      flex-wrap: wrap;

      i {
        color: $color-cool-grey-500;
        font-size: 20px;
      }

      .txt-radio-name {
        color: $color-cool-grey-700;
        white-space: normal;
        word-wrap: break-word;
        max-width: 250px;
      }
    }

    .txt-radio-desc {
      color: $color-cool-grey-500;
      cursor: default;
      white-space: normal;
      word-wrap: break-word;
      max-width: 250px;
    }

    &:focus .el-radio__inner{
        box-shadow: 0 0 0 0 !important;
        outline-offset: $space-4xs;
        outline: $space-4xs solid $color-primary-200;
    }

    &:focus-visible .el-radio__inner{
        box-shadow: 0 0 0 0 !important;
        outline-offset: $space-4xs;
        outline: $space-4xs solid $color-primary-200;
    }

    &.is-checked {
      .el-radio__inner {
        border-color: $color-primary-500;
        background-color: $color-primary-500;
      }
    }

    .el-radio__label {
      padding-left: $spacing-sm;
      color: $color-cool-grey-700;
    }

    &.medium {
      .txt-radio-name {
        font: $font-style-body-lg-reg;
      }

      .txt-radio-desc {
        font: $font-style-body-md-reg;
      }

      .el-radio__input {
        vertical-align: middle;
      }

      .el-radio__inner {
        width: 20px !important;
        height: 20px !important;
      }

      .el-radio__inner::after {
        width: 8px !important;
        height: 8px !important;
      }
    }

    &.small {
      .txt-radio-name {
        font: $font-style-body-md-reg;
      }

      .txt-radio-desc {
        font: $font-style-body-sm-reg;
      }
      
      .el-radio__input {
        vertical-align: middle;
        margin-top: 2px;
      }

      .el-radio__inner {
        width: 14px !important;
        height: 14px !important;
      }

      .el-radio__inner::after {
        width: 6px !important;
        height: 6px !important;
      }

      i {
        color: $color-cool-grey-500;
        font-size: 14px;
      }
    }

    &.is-disabled {
      .el-radio__input {
        .el-radio__inner {
          border-color: $color-cool-grey-300;
          background-color: $color-cool-grey-100;
        }

        &.is-checked {
          .el-radio__inner {
            border-color: $color-cool-grey-300;
            background-color: $color-cool-grey-300;
          }

          .el-radio__inner::after {
            background-color: $color-cool-grey-100 !important;
          }
        }
      }

      .fragment-name .txt-radio-name {
        color: $color-cool-grey-300;
      }

      .txt-radio-desc {
        color: $color-cool-grey-300;
      }

      &:hover {
        .el-radio__input {
          .el-radio__inner {
            border-color: $color-cool-grey-300;
            background-color: $color-cool-grey-100;
          }

          &.is-checked {
            .el-radio__inner {
              border-color: $color-cool-grey-300;
              background-color: $color-cool-grey-300;
            }

            .el-radio__inner::after {
              background-color: $color-cool-grey-100 !important;
            }
          }
        }
      }

      &:focus .el-radio__inner{
        outline: $space-4xs solid transparent;
      }

      &:focus-visible .el-radio__inner{
        outline: $space-4xs solid transparent;
      }
    }

    &.read-only {
      .el-radio__input {
        .el-radio__inner {
          border-color: $color-cool-grey-300;
          background-color: $color-cool-grey-100;
        }

        &.is-checked {
          .el-radio__inner {
            border-color: $color-cool-grey-200;
            background-color: $color-primary-200;
          }
        }
      }

      &:hover {
        .el-radio__input {
          .el-radio__inner {
            border-color: $color-cool-grey-300;
            background-color: $color-cool-grey-100;
          }

          &.is-checked {
            .el-radio__inner {
              border-color: $color-cool-grey-200;
              background-color: $color-primary-200;
            }
          }
        }
      }
    }

    &:hover {
      .el-radio__input {
        .el-radio__inner {
          border-color: $color-cool-grey-500;
        }

        &.is-checked {
          .el-radio__inner {
            border-color: $color-primary-700;
            background-color: $color-primary-700;
          }
        }
      }
    }

    &.is-error {
      .el-radio__input {
        .el-radio__inner {
          border-color: $color-semanticRed-500;
        }

        &.is-checked { 
          .el-radio__inner {
            border-color: $color-semanticRed-500;
            background-color: $color-white;
          }

          .el-radio__inner::after {
            background-color: $color-semanticRed-500 !important;
          }
        }
      }

      &:hover {
        .el-radio__input {
          .el-radio__inner {
            border-color: $color-semanticRed-700;
          }

          &.is-checked {
            .el-radio__inner {
              border-color: $color-semanticRed-700;
              background-color: $color-white;
            }

            .el-radio__inner::after {
              background-color: $color-semanticRed-700 !important;
            }
          }
        }
      }
    }
  }
}</style>
